import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { parse as parseCurl, type RequestSpec } from "@rest/curl";
import { api } from "../lib/api";
import { LoadingButton } from "./Spinner";

interface Props {
  collectionId: string;
  folderId?: string | null;
  onClose: () => void;
}

// Paste-curl dialog. Parses on-the-fly so the user sees what they'll get
// before they commit. Submit → create a new request in this collection /
// folder pre-populated from the parsed spec.
export default function CurlImport({ collectionId, folderId, onClose }: Props) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [src, setSrc] = useState("");
  const [name, setName] = useState("");

  const parsed = useMemo<RequestSpec | null>(() => {
    if (!src.trim()) return null;
    try { return parseCurl(src, { name: name.trim() || "Imported request" }); }
    catch { return null; }
  }, [src, name]);

  const create = useMutation({
    mutationFn: async () => {
      if (!parsed) throw new Error("nothing to import");
      const r = await api.requests.create({
        collection_id: collectionId,
        name: parsed.name,
        method: parsed.method,
        url: parsed.url,
        headers: parsed.headers,
        query: parsed.query,
        body_type: parsed.bodyType === "multipart" ? "form" : parsed.bodyType,
        body: parsed.body,
      });
      // Apply folder + protocol + any extras via PATCH so we don't bloat the
      // create endpoint signature.
      await api.requests.update(r.id, {
        folder_id: folderId ?? null,
        protocol: "rest",
        note: "imported from curl",
      });
      return r;
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["requests", collectionId] });
      if (folderId) qc.invalidateQueries({ queryKey: ["folders", collectionId] });
      onClose();
      nav(`/r/${r.id}`);
    },
  });

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center p-4" style={{ background: "var(--backdrop)" }} onClick={onClose}>
      <div className="card w-full max-w-3xl shadow-[var(--shadow-strong)] p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Import from curl</h2>
          <button className="btn" onClick={onClose}>Close</button>
        </div>

        <p className="text-sm muted">
          Paste a <code>curl</code> command — supports Chrome / Firefox / Safari "Copy as cURL", Stripe-style docs,
          multi-line backslash continuations, <code>-XPOST</code> compounds, <code>-F</code> multipart, <code>-G --data-urlencode</code>, and basic auth via <code>-u</code>.
        </p>

        <div>
          <label className="label">Curl command</label>
          <textarea
            className="input input-mono mt-1 min-h-[160px]"
            value={src}
            onChange={(e) => setSrc(e.target.value)}
            spellCheck={false}
            placeholder={`curl 'https://api.example.com/v1/widgets' \\\n  -H 'authorization: Bearer abc' \\\n  -H 'content-type: application/json' \\\n  --data-raw '{"name":"Ada"}'`}
            autoFocus
          />
        </div>

        {parsed && (
          <div className="rounded-md border border-border bg-surface-2 p-3 text-sm space-y-2">
            <div className="flex items-center gap-2">
              <span className={"method-tag " + parsed.method.toLowerCase()}>{parsed.method}</span>
              <code className="text-xs muted break-all">{parsed.url}</code>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="label">Headers</div>
                {parsed.headers.length === 0 && <p className="muted-strong">None.</p>}
                <ul className="mono space-y-0.5">
                  {parsed.headers.map((h, i) => (
                    <li key={i} className="truncate"><span className="muted">{h.key}:</span> {h.value}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="label">Query</div>
                {parsed.query.length === 0 && <p className="muted-strong">None.</p>}
                <ul className="mono space-y-0.5">
                  {parsed.query.map((q, i) => (
                    <li key={i} className="truncate"><span className="muted">{q.key}=</span>{q.value}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div>
              <div className="label">Body ({parsed.bodyType}{parsed.bodyType !== "none" && parsed.body ? ` · ${parsed.body.length} chars` : ""})</div>
              {parsed.body && (
                <pre className="code-block mt-1 max-h-24 overflow-y-auto">{parsed.body.length > 600 ? parsed.body.slice(0, 600) + "…" : parsed.body}</pre>
              )}
            </div>
            {parsed.auth.kind !== "none" && (
              <div className="text-xs muted-strong">
                Auth: <b className="text-text">{parsed.auth.kind}</b>{parsed.auth.kind === "basic" && parsed.auth.fields.username ? ` as ${parsed.auth.fields.username}` : ""}
              </div>
            )}
            {parsed.unsupported.length > 0 && (
              <p className="text-xs text-warn">
                Ignored flags: <code>{parsed.unsupported.slice(0, 6).join(", ")}</code>
                {parsed.unsupported.length > 6 ? ` (+${parsed.unsupported.length - 6} more)` : ""}
              </p>
            )}
          </div>
        )}

        <div>
          <label className="label">Request name (optional)</label>
          <input
            className="input mt-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={parsed ? `${parsed.method} ${new URL(parsed.url || "https://x/").pathname}` : "Imported request"}
          />
        </div>

        {create.isError && (
          <p className="text-xs text-danger">{String((create.error as Error).message)}</p>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <LoadingButton
            type="button"
            className="btn-primary"
            pending={create.isPending}
            disabled={!parsed?.url}
            onClick={() => create.mutate()}
          >
            Import
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}
